<template>
	<div class="flex flex-grow" v-if="isUserAllowedInLevel5or6">
		<ProcessCreatePage v-if="!showStep2 && !showStep3">
			<Header :title="$t('new_vehicle_overview_image').toString()">
				<p v-if="isAppContainer">
					{{ $t('take_image_of_vehicle') }}<br />
					{{ $t('tablet_horizontal') }}
				</p>
				<p v-else>
					{{ $t('upload_image_of_vehicle') }}
				</p>
			</Header>

			<div class="main">
				<div class="flex justify-center mb-4" v-if="overviewImage">
					<img :src="overviewImage" :alt="$t('overview_image')" class="mb-4 h-auto md:h-56" />
				</div>
				<BaseCamera
					orientation="landscape"
					@snapshot="overviewImage = $event"
					:label="$t('add_overview_image')"
					:take-image-label="$t('activate_camera')"
					:remove-upload-button="isAppContainer"
					:take-image="true"
					:allow-upload-in-app-container="false"
					:enable-preview="false"
				/>
			</div>

			<Footer
				@backButtonClick="goBack"
				@skipButtonClick="continueButtonClick(2, true)"
				@continueButtonClick="continueButtonClick(2, false)"
				:is-back-button="false"
				:is-continue-button="true"
				:is-skippable="!overviewImage"
				class="m-8"
			/>
		</ProcessCreatePage>
		<ProcessCreatePage v-if="showStep2 && !showStep3">
			<Header :title="$t('new_vehicle_scan_vin').toString()">
				<p>
					<span v-if="isAppContainer">{{ $t('vehicle_vin_take_photo') }}</span>
					<span v-else>{{ $t('vehicle_vin_upload_photo') }}</span>
					<span>. {{ $t('vehicle_vin_take_photo_placement') }}</span>
					<br />
					{{ $t('vehicle_registration_system_analysis') }}<br />
				</p>
			</Header>
			<div class="main">
				<div v-if="!isLoading && showEmpty"></div>
				<div v-show="!isLoading && !showEmpty">
					<div class="flex justify-center mb-4" v-if="vinImage">
						<BaseImage :src="vinImage" :alt="$t('vin')" :rotation="rotation" @tempDoc="setRotatedVinImage" class="mb-4 h-auto md:h-56" />
					</div>
					<div class="flex flex-grow w-full mb-4" v-if="vinImage">
						<div class="mr-4">
							{{ $t('rotate_image_hint') }}
						</div>
						<div @click="rotation === 270 ? (rotation = 0) : (rotation += 90)" class="h-8 cursor-pointer">
							<font-awesome-icon :icon="['fas', 'sync-alt']" class="text-connectProBlue justify-start" size="lg" />
						</div>
					</div>
					<!-- currently disabled as api not working without payment.
					<div v-if="vinImage" class="flex justify-start mb-4">
						<BaseButton class="btn-blue" :disabled="isLoading" @click="analyzePreparedVinImage">
							{{ $t('analyse_vin') }}
						</BaseButton>
					</div>
					-->
					<VehicleVinAnalyzer
						class="mt-8"
						ref="vinAnalyzer"
						@vehicleVinImageContent="setVinImageContent($event)"
						:show-success-message="false"
						v-show="!vinImage"
						@showEmpty="
							showEmpty = true;
							isLoading = false;
						"
						@hideEmpty="showEmpty = false"
						@snapshot="prepareVinImage"
						:is-success-analyze-require="false"
						:defer-analysis="true"
					/>
					<div class="flex flex-col w-1/2 pr-0 md:pr-4 mb-4">
						<BaseInput
							ref="BaseInput.value.valueIdentificationNumber"
							v-model="vehicle.vehicleIdentificationNumber"
							:required="true"
							class="w-full mb-0"
							:error-message="$t('enter_valid_vin')"
							:label="$t('vin')"
							mask="FFFFFFFFFFFFFFFFF"
							maxlength="17"
							:placeholder="$t('vin')"
							type="text"
							validation="[0-9a-zA-Z]{0,}"
							:disabled="false"
							@input="updateVehicleIdentificationNumber"
						/>
						<div class="flex justify-between text-sm">
							<div v-if="vinShorterMax">{{ $t('vin_shorter_max') }}</div>
						</div>
					</div>
				</div>
			</div>
			<LoadingSpinnerComponent v-if="isLoading" />
			<Footer
				@backButtonClick="goToStep(1)"
				:disable-buttons="isLoading"
				@continueButtonClick="continueButtonClick(3, false)"
				:is-back-button="true"
				:is-continue-button="true"
				:is-skippable="false"
				class="m-8"
			/>
		</ProcessCreatePage>
		<ProcessCreatePage v-if="showStep3 && !showStep4">
			<Header :title="$t('new_vehicle_add_registration').toString()">
				<p>
					<span v-if="isAppContainer">{{ $t('vehicle_registration_take_photo_front') }}</span>
					<span v-else>{{ $t('vehicle_registration_upload_photo_front') }}</span>
				</p>
			</Header>
			<div class="main">
				<div class="flex justify-center mb-4" v-if="vehicleRegistrationImageContent">
					<img :src="vehicleRegistrationImageContent" :alt="$t('vehicle_registration')" class="mb-4 h-auto md:h-56" />
				</div>
				<BaseCamera
					orientation="landscape"
					@snapshot="vehicleRegistrationImageContent = $event"
					:label="$t('add_vehicle_registration')"
					:take-image-label="$t('add_vehicle_registration')"
					:remove-upload-button="isAppContainer"
					:take-image="true"
					:allow-upload-in-app-container="false"
					:enable-preview="false"
				/>
			</div>

			<Footer
				@backButtonClick="goToStep(2)"
				:disable-buttons="isLoading"
				@continueButtonClick="continueButtonClick(4, false)"
				@skipButtonClick="continueButtonClick(4, true)"
				:is-back-button="true"
				:is-continue-button="true"
				:is-skippable="true"
				class="m-8"
			/>
		</ProcessCreatePage>
		<ProcessCreatePage v-if="showStep4">
			<Header :title="$t('new_vehicle_data_input').toString()">
				<p>
					{{ $t('required_data_red_stars') }}
				</p>
			</Header>
			<div class="main">
				<div v-if="vehicleRegistrationImageContent" class="flex justify-center">
					<BaseImage :src="vehicleRegistrationImageContent" :alt="$t('vehicle_registration')" class="object-contain" />
				</div>
				<CustomerVehicleForm ref="CustomerVehicleForm" v-model="vehicle" :display="true" :is-customer-car="true" @updateValue="vehicle = $event" />
			</div>

			<Footer @backButtonClick="goToStep(3)" :is-continue-button="false" :is-skippable="false" :is-back-button="true" class="m-8">
				<template #right>
					<BaseButton @click="save" :disabled="isLoading" class="mr-4 btn-blue">{{ $t('save_vehicle') }}</BaseButton>
				</template>
			</Footer>
		</ProcessCreatePage>
	</div>
	<Forbidden v-else />
</template>

<script>
import CustomerVehicleForm from '@/components/forms/CustomerVehicleForm';
import Footer from '@/components/damagecases/Footer';
import { VehiclesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseButton from '@/components/forms/buttons/BaseButton';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Header from '@/components/damagecases/Header';
import BaseCamera from '@/components/forms/inputs/BaseCamera';
import BaseImage from '@/components/forms/BaseImage';
import ProcessCreatePage from '@/views/management/Process/ProcessCreatePage';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';
import { parseGermanDate } from '@/plugins/Utils';
import VehicleVinAnalyzer from '@/components/forms/VehicleVinAnalyzer.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import LoadingSpinnerComponent from '@/components/navigation/LoadingSpinner';
import Validate from '@/mixins/Validate';
export default {
	name: 'Create',
	components: {
		LoadingSpinnerComponent,
		BaseInput,
		Forbidden,
		ProcessCreatePage,
		BaseImage,
		BaseCamera,
		Header,
		BaseButton,
		VehicleVinAnalyzer,
		Footer,
		CustomerVehicleForm,
	},
	mixins: [AppContainerMixin, Validate, LoadingSpinner, Authentication],
	data() {
		return {
			overviewImage: null,
			overviewImageAccepted: false,
			skipOverviewImage: false,
			vehicle: { vehicleIdentificationNumber: '' },
			vehicleRegistrationImage: null,
			vinImage: null,
			vinImageAccepted: false,
			skipVehicleRegistrationImage: null,
			vehicleRegistrationImageContent: null,
			showOverview: false,
			isLoading: false,
			checkedVinContent: false,
			rotation: 0,
			showEmpty: false,
			tempDoc: null,
		};
	},
	mounted() {
		if (this.isUserAllowedInLevel5or6) {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		}
	},
	destroyed() {
		this.$store.commit('setConfirmLeaving', false);
	},
	computed: {
		showStep2() {
			return (this.overviewImageAccepted || this.skipOverviewImage) && !this.vehicleRegistrationImageContent;
		},
		showStep3() {
			return (this.vinImage || this.vehicle.vehicleIdentificationNumber) && this.checkedVinContent;
		},
		showStep4() {
			return this.vehicleRegistrationImageContent || this.skipVehicleRegistrationImage;
		},
		vinShorterMax() {
			if (this.vehicle === undefined) {
				return false;
			}
			return this.vehicle?.vehicleIdentificationNumber?.toString().length < 17;
		},
	},
	methods: {
		goToStep(step) {
			if (step === 1) {
				this.skipOverviewImage = false;
				this.vehicleRegistrationImageContent = null;
				this.overviewImageAccepted = false;
			} else if (step === 2) {
				this.vinImage = null;
				this.checkedVinContent = false;
			} else if (step === 3) {
				this.skipVehicleRegistrationImage = null;
				this.vehicleRegistrationImageContent = null;
			}
		},
		goBack() {
			if (this.$route.params.ref) {
				if (this.$route.params.ref === 'customerCreate') {
					this.$router.push({ name: 'management.customer.create', params: { customer: this.$route.params.customer } });
				}
			} else {
				this.$router.go(-1);
			}
		},
		save() {
			if (this.$refs.CustomerVehicleForm.validate()) {
				this.isLoading = true;
				this.setLoadingStatus(true);
				this.vehicle.customer = { id: this.$route.params.customer };
				this.vehicle.overviewImage = this.overviewImage;
				if (this.tempDoc) {
					this.vinImage = this.tempDoc;
				}
				this.vehicle.vinImage = this.vinImage;
				this.vehicle.vehicleRegistrationImage = { image: this.vehicleRegistrationImageContent };
				VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.vehiclesPost(this.vehicle)
					.then(response => {
						this.$store.commit('setConfirmLeaving', false);
						this.$swal({
							title: this.$t('general.success'),
							text: this.$t('vehicle_stored'),
							icon: 'success',
						}).then(() => {
							this.$router.push({ path: '/management/customer/overview/' + this.$route.params.customer });
							this.isLoading = false;
						});
					})
					.catch(error => {
						this.isLoading = false;
						if (error.response.status === 409) {
							this.$swal(this.$t('general.error').toString(), this.$t('vin_exists').toString(), 'error');
						} else {
							this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
						}
					})
					.finally(() => this.setLoadingStatus(false));
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		updateVehicleIdentificationNumber(value) {
			if (this.vehicle.vehicleIdentificationNumber.includes('O')) {
				this.vehicle.vehicleIdentificationNumber = this.vehicle.vehicleIdentificationNumber.replace('O', '0');
			}
		},
		setVinImageContent(event) {
			if (event.VIN) {
				this.vehicle.vehicleIdentificationNumber = event.VIN;
			}
			this.vinImage = event.image;
			this.tempDoc = null;
			this.isLoading = false;
		},
		setVehicleRegistrationImageContent: function($event) {
			this.vehicleRegistrationImage = $event.image;
			this.vehicleRegistrationImageContent = $event;
			if ($event.vin) {
				this.vehicle.vehicleIdentificationNumber = $event.vin;
			}
			if ($event.manufacturer && !$event.vin) {
				this.vehicle.manufacturer = $event.manufacturer;
			}
			if ($event.licensePlate) {
				this.vehicle.licensePlate = $event.licensePlate;
			}
			if ($event.model) {
				this.vehicle.model = $event.model;
			}
			if ($event.fuel) {
				this.vehicle.fuel = $event.fuel;
			}
			if ($event.registrationFirst) {
				this.vehicle.registrationFirst = new Date(parseGermanDate($event.registrationFirst));
			}
			if ($event.displacement) {
				this.vehicle.displacement = $event.displacement;
			}
			if ($event.exhaustKey) {
				this.vehicle.exhaustKey = $event.exhaustKey;
			}
			if ($event.kw) {
				this.vehicle.kw = $event.kw;
			}
			//this.continueButtonClick(false);
		},
		prepareVinImage(img) {
			this.vinImage = img;
			this.tempDoc = null;
			this.rotation = 0;
			this.isLoading = false;
		},
		analyzePreparedVinImage() {
			const imageToAnalyze = this.tempDoc || this.vinImage;
			if (!imageToAnalyze || !this.$refs.vinAnalyzer) {
				return;
			}
			this.isLoading = true;
			this.$refs.vinAnalyzer.analyzeVehicleRegistration(imageToAnalyze);
		},
		setRotatedVinImage(rotatedImage) {
			this.tempDoc = rotatedImage;
			this.vinImage = rotatedImage;
			this.rotation = 0;
		},
		continueButtonClick: function(nextStep, isSkip) {
			if (isSkip) {
				if (nextStep === 2) {
					this.skipOverviewImage = true;
				} else if (nextStep === 4) {
					this.skipVehicleRegistrationImage = true;
				}
			} else if (this.validate()) {
				if (nextStep === 2) {
					this.$swal({
						title: this.$t('store_mileage').toString(),
						text: this.$t('update_vehicles_mileage').toString(),
						icon: 'question',
						input: 'number',
						inputValue: this.vehicleKilometrage,
						confirmButtonText: this.$t('update').toString(),
						cancelButtonText: this.$t('general.no').toString(),
						inputPlaceholder: '1244',
						validationMessage: this.$t('enter_valid_number').toString(),
						inputAttributes: {
							minNumber: 1,
							maxLength: 6,
						},
						inputValidator: inputValue => {
							if (isNaN(inputValue)) {
								return this.$t('enter_valid_number');
							} else if (!inputValue) {
								return this.$t('enter_number');
							}
						},
						showCancelButton: true,
					}).then(result => {
						if (result.value && result.isConfirmed) {
							this.vehicle.kilometrage = result.value;
						}
						this.overviewImageAccepted = true;
					});
				}
				if (nextStep === 3) {
					if (this.vehicle.vehicleIdentificationNumber) {
						this.checkedVinContent = true;
					} else {
						this.$swal(this.$t('general.attention').toString(), this.$t('enter_valid_vin').toString(), 'warning');
					}
				}
				if (nextStep === 4) {
					this.skipVehicleRegistrationImage = true;
				}
			} else if (nextStep === 3 && this.vinImage == null && !this.vehicle.vehicleIdentificationNumber) {
				this.$swal(this.$t('general.attention').toString(), this.$t('vehicle_vin_take_photo').toString(), 'warning');
			}
		},
		validate: function() {
			return !!this.overviewImage || !!this.vehicleRegistrationImageContent || this.vinImage || this.vehicle.vehicleIdentificationNumber;
		},
	},
};
</script>

<style scoped></style>
